import React, { useRef, useState, useCallback } from 'react';
import { MoveHorizontal } from 'lucide-react';

/**
 * Draggable before/after image comparison slider.
 * - `beforeSrc` shows full width; `afterSrc` is revealed from the left as the handle moves right.
 * - Pointer + touch + keyboard (← →) supported.
 */
const BeforeAfterSlider = ({
  beforeSrc,
  afterSrc,
  beforeLabel = 'Before',
  afterLabel = 'After',
  alt = '',
  aspect = '16 / 10',
  testId,
}) => {
  const containerRef = useRef(null);
  const draggingRef = useRef(false);
  const [pos, setPos] = useState(50);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    let p = ((clientX - rect.left) / rect.width) * 100;
    p = Math.max(0, Math.min(100, p));
    setPos(p);
  }, []);

  const onPointerDown = (e) => {
    draggingRef.current = true;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
  };
  const onPointerMove = (e) => {
    if (draggingRef.current) setFromClientX(e.clientX);
  };
  const stop = () => { draggingRef.current = false; };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowLeft') setPos((p) => Math.max(0, p - 4));
    else if (e.key === 'ArrowRight') setPos((p) => Math.min(100, p + 4));
  };

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      role="slider"
      aria-label={`${afterLabel} vs ${beforeLabel} comparison`}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(pos)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={stop}
      onPointerCancel={stop}
      onKeyDown={onKeyDown}
      className="relative w-full overflow-hidden select-none cursor-ew-resize touch-none bg-black outline-none focus:ring-2 focus:ring-[#00FFD1]/60"
      style={{ aspectRatio: aspect }}
    >
      {/* Before image (full) */}
      <img
        src={beforeSrc}
        alt={alt ? `${alt} — ${beforeLabel}` : beforeLabel}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        draggable={false}
      />
      {/* After image (revealed from the left) */}
      <img
        src={afterSrc}
        alt={alt ? `${alt} — ${afterLabel}` : afterLabel}
        className="absolute inset-0 w-full h-full object-cover pointer-events-none"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable={false}
      />

      {/* Labels — the dominant side lights up, the other darkens as you drag */}
      <span
        className={`absolute top-2 left-2 text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-1 rounded pointer-events-none transition-all duration-200 ${
          pos >= 50
            ? 'bg-[#00FFD1] text-black shadow-[0_0_16px_rgba(0,255,209,0.9)] scale-105'
            : 'bg-black/55 text-white/30'
        }`}
        data-testid={testId ? `${testId}-after-label` : undefined}
        data-active={pos >= 50}
      >
        {afterLabel}
      </span>
      <span
        className={`absolute top-2 right-2 text-[10px] font-mono font-bold uppercase tracking-wider px-2 py-1 rounded pointer-events-none transition-all duration-200 ${
          pos < 50
            ? 'bg-white text-black shadow-[0_0_16px_rgba(255,255,255,0.75)] scale-105'
            : 'bg-black/55 text-white/30'
        }`}
        data-testid={testId ? `${testId}-before-label` : undefined}
        data-active={pos < 50}
      >
        {beforeLabel}
      </span>

      {/* Divider + handle */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-[#00FFD1] pointer-events-none"
        style={{ left: `${pos}%`, transform: 'translateX(-50%)', boxShadow: '0 0 12px rgba(0,255,209,0.7)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-[#00FFD1] text-black flex items-center justify-center shadow-[0_0_18px_rgba(0,255,209,0.7)]">
          <MoveHorizontal size={18} />
        </div>
      </div>
    </div>
  );
};

export default BeforeAfterSlider;
